import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const token = localStorage.getItem("token");
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("LAS CONTRASEÑAS NO COINCIDEN");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("LA CONTRASEÑA DEBE TENER AL MENOS 6 CARACTERES");
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("CONTRASEÑA ACTUALIZADA");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CAMBIAR CONTRASEÑA");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "ELIMINAR") {
      toast.error("ESCRIBE 'ELIMINAR' PARA CONFIRMAR");
      return;
    }
    
    setDeletingAccount(true);
    
    try {
      await axios.delete(`${API}/auth/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("CUENTA ELIMINADA");
      logout();
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL ELIMINAR CUENTA");
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-zinc-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-mono text-white">CONFIGURACIÓN</h1>
        </div>

        {/* User Info */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6 mb-6">
          <h2 className="text-lg font-mono text-white mb-4">TU CUENTA</h2>
          <div className="space-y-2 text-zinc-400 font-mono text-sm">
            <p>Email: <span className="text-white">{user?.email}</span></p>
            <p>Nombre: <span className="text-white">{user?.name}</span></p>
            <p>Tipo: <span className="text-white uppercase">{user?.user_type}</span></p>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6 mb-6">
          <h2 className="text-lg font-mono text-white mb-4">CAMBIAR CONTRASEÑA</h2>
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="text-zinc-400 font-mono text-xs">CONTRASEÑA ACTUAL</Label>
              <div className="relative mt-1">
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white font-mono pr-10"
                  data-testid="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
                >
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-400 font-mono text-xs">NUEVA CONTRASEÑA</Label>
              <Input
                type={showPasswords ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-zinc-900 border-zinc-700 text-white font-mono mt-1"
                data-testid="new-password"
                required
              />
            </div>
            
            <div>
              <Label className="text-zinc-400 font-mono text-xs">CONFIRMAR NUEVA CONTRASEÑA</Label>
              <Input
                type={showPasswords ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-zinc-900 border-zinc-700 text-white font-mono mt-1"
                data-testid="confirm-password"
                required
              />
            </div>
            
            <Button
              type="submit"
              disabled={changingPassword}
              className="w-full bg-primary text-white font-mono"
              data-testid="change-password-btn"
            >
              {changingPassword ? "ACTUALIZANDO..." : "CAMBIAR CONTRASEÑA"}
            </Button>
          </form>
        </div>

        {/* Delete Account */}
        <div className="bg-zinc-900/50 border border-red-900/50 p-6">
          <h2 className="text-lg font-mono text-red-500 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            ZONA DE PELIGRO
          </h2>
          
          {!showDeleteConfirm ? (
            <div>
              <p className="text-zinc-400 font-mono text-sm mb-4">
                Eliminar tu cuenta es permanente. Se borrarán todos tus datos, eventos, y configuraciones.
              </p>
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white font-mono"
                data-testid="show-delete-btn"
              >
                ELIMINAR CUENTA
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-red-400 font-mono text-sm">
                ¿Estás seguro? Esta acción NO se puede deshacer.
              </p>
              <p className="text-zinc-400 font-mono text-sm">
                Escribe <span className="text-red-500 font-bold">ELIMINAR</span> para confirmar:
              </p>
              <Input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                className="bg-zinc-900 border-red-700 text-white font-mono"
                placeholder="ELIMINAR"
                data-testid="delete-confirm-input"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText("");
                  }}
                  className="flex-1 border-zinc-700 text-zinc-400 font-mono"
                >
                  CANCELAR
                </Button>
                <Button
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount || deleteConfirmText !== "ELIMINAR"}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-mono"
                  data-testid="confirm-delete-btn"
                >
                  {deletingAccount ? "ELIMINANDO..." : "CONFIRMAR ELIMINACIÓN"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
